import React, { useEffect, useState, useCallback, useMemo, Suspense } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "lib/supabaseClient";
import { insertLog } from "services/logService";
import "flatpickr/dist/flatpickr.css";
import {
  MdEdit, MdPerson, MdBadge, MdFingerprint, MdWork, MdAttachMoney,
  MdPhone, MdEmail, MdFlightTakeoff, MdHistory,
  MdCancel, MdCloudUpload, MdInsertDriveFile, MdDelete,
  MdAccountTree, MdPeople, MdAssignmentInd,
  MdDeleteSweep, MdPrint, MdDateRange, MdComment, MdTerminal,
  MdDescription, MdSettingsSuggest, MdLockReset, MdTimer
} from "react-icons/md";

const FlatpickrLazy = React.lazy(() => import("react-flatpickr"));

const ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InhkcXZrZXd3ZmlsbHZmbGRrb3RtIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzA0NjgwMjMsImV4cCI6MjA4NjA0NDAyM30.bFLj9EpcLb3S8uB_3X_Dw0J03IqiQf36zkSQ8Mi0ZpM";
const FN_URL   = "https://xdqvkewwfillvfldkotm.supabase.co/functions/v1";

// ─── Helpers ───────────────────────────────────────────────
const calcDays = (s, e) => {
  if (!s || !e) return 0;
  const start = new Date(s); const end = new Date(e);
  start.setHours(0,0,0,0); end.setHours(0,0,0,0);
  const d = Math.floor((end - start) / 86400000) + 1;
  return d > 0 ? d : 0;
};

const fmt = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return isNaN(dt) ? "—" : dt.toLocaleDateString("en-GB");
};

const toSQL = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const statusStyle = (s) => ({
    Active:            "bg-green-50 text-green-600 border border-green-200",
    "Annual Leave":    "bg-blue-50 text-blue-600 border border-blue-200",
    "Sick Leave":      "bg-purple-50 text-purple-600 border border-purple-200",
    "Emergency Leave": "bg-orange-50 text-orange-600 border border-orange-200",
  }[s] || "bg-gray-100 text-gray-500 border border-gray-200");

// ─── callFn — direct fetch to edge function ──────────────
const callFn = async (name, body) => {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token || ANON_KEY;
  const res = await fetch(`${FN_URL}/${name}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "apikey": ANON_KEY,
      "Authorization": `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || "Function error");
  return json;
};

// ═══════════════════════════════════════════════════════════
export default function ProfessionalProfile() {
  const { search } = useLocation();
  const navigate   = useNavigate();
  const params     = new URLSearchParams(search);
  const employeeId   = params.get("id");
  const employeeName = params.get("name");

  const [empData,      setEmpData]      = useState(null);
  const [tempData,     setTempData]     = useState(null);
  const [logs,         setLogs]         = useState([]);
  const [leaveHistory, setLeaveHistory] = useState([]);
  const [currentUser,  setCurrentUser]  = useState(null);
  const [isEditing,    setIsEditing]    = useState(false);
  const [isLoading,    setIsLoading]    = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [leaveReq,     setLeaveReq]     = useState({ startDate: "", endDate: "", type: "Annual Leave", reason: "" });
  const [leaveFile,    setLeaveFile]    = useState(null);
  const [showPwdModal, setShowPwdModal] = useState(false);
  const [newPwd,       setNewPwd]       = useState("");
  const [confirmPwd,   setConfirmPwd]   = useState("");
  const [pwdErr,       setPwdErr]       = useState("");

  const isAdmin = useMemo(() =>
    currentUser?.role === "admin" || currentUser?.id === "6f226d92-8217-415b-991a-69da541ed483",
    [currentUser]
  );

  // ── Fetch ──
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return navigate("/auth/sign-in");
      const { data: me } = await supabase.from("employees").select("*").eq("id", user.id).single();
      setCurrentUser(me);

      let targetId = employeeId;
      if (!targetId && employeeName) {
        const { data: found } = await supabase.from("employees").select("id").ilike("full_name", employeeName).single();
        targetId = found?.id;
      }
      if (!targetId) return;

      const [empRes, logsRes, leavesRes] = await Promise.all([
        supabase.from("employees").select("*").eq("id", targetId).single(),
        supabase.from("employee_logs").select("*").eq("employee_id", targetId).order("created_at", { ascending: false }).limit(100),
        supabase.from("employee_leaves").select("*").eq("employee_id", targetId).order("created_at", { ascending: false }),
      ]);

      const emp = empRes.data;
      setEmpData(emp);
      setTempData(emp);
      setLogs(logsRes.data || []);
      setLeaveHistory(leavesRes.data || []);
      setIsAuthorized(true);

      // Auto-fix: إذا الموظف status != Active وما في إجازة نشطة
      if (emp && emp.status !== "Active") {
        const today = new Date().toISOString().split("T")[0];
        const { data: activeLeaves } = await supabase
          .from("employee_leaves").select("id")
          .eq("employee_id", emp.id).eq("status", "approved")
          .lte("start_date", today).gte("end_date", today);
        if (!activeLeaves || activeLeaves.length === 0) {
          await supabase.from("employees")
            .update({ status: "Active", is_on_leave: false, last_leave_end_date: null })
            .eq("id", emp.id);
          emp.status = "Active";
          emp.is_on_leave = false;
          emp.last_leave_end_date = null;
          setEmpData({...emp});
          setTempData({...emp});
        }
      }
    } catch (err) { console.error(err); }
    finally { setIsLoading(false); }
  }, [employeeId, employeeName, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const logActivity = async (action, details) => {
    // 1. Log to Employee Profile History
    await supabase.from("employee_logs").insert([{
      employee_id: empData.id, action_type: action,
      details: `[BY: ${currentUser.full_name}] ${details}`,
    }]);

    // 2. Log to Security Logs (Global)
    try {
      await insertLog({
        timestamp:     new Date().toISOString(),
        actor_name:    currentUser.full_name,
        actor_number:  currentUser.employee_number,
        actor_role:    currentUser.role,
        target_id:     empData.id,
        target_name:   empData.full_name,
        target_number: empData.employee_number,
        action,
        details,
      });
    } catch {}

    fetchData();
  };

  // ── ① Reset Password via Edge Function ──
  const handleResetPassword = async () => {
    setPwdErr("");
    if (!newPwd || newPwd.length < 6) { setPwdErr("Password must be at least 6 characters."); return; }
    if (newPwd !== confirmPwd) { setPwdErr("Passwords do not match."); return; }
    setIsLoading(true);
    try {
      await callFn("reset-employee-password", { user_id: empData.id, new_password: newPwd });
      await logActivity("RESET_PASSWORD", `Password reset for ${empData.full_name}`);
      setShowPwdModal(false); setNewPwd(""); setConfirmPwd("");
      alert(`✅ Password updated for ${empData.full_name}`);
    } catch (err) { setPwdErr(err.message); }
    finally { setIsLoading(false); }
  };

  // ── ② Leave Request ──
  const handleLeaveRequest = async () => {
    const days = calcDays(leaveReq.startDate, leaveReq.endDate);
    if (days <= 0) return alert("Invalid date range.");
    setIsLoading(true);
    try {
      let docUrl = null;
      let newAssets = empData.contract_assets || [];
      if (leaveFile) {
        const path = `${empData.id}/L_${Date.now()}_${leaveFile.name}`;
        await supabase.storage.from("contracts").upload(path, leaveFile);
        const { data: { publicUrl } } = supabase.storage.from("contracts").getPublicUrl(path);
        docUrl = publicUrl;
        newAssets = [...newAssets, { name: `Leave_${leaveReq.type}_${leaveReq.startDate}`, url: publicUrl, path, date: new Date().toISOString() }];
      }

      const upd = { ...empData };
      if (leaveReq.type === "Annual Leave")    { upd.annual_leave_balance -= days; upd.annual_leave_used = (upd.annual_leave_used || 0) + days; }
      if (leaveReq.type === "Sick Leave")      { upd.sick_leave_count = (upd.sick_leave_count || 0) + days; }
      if (leaveReq.type === "Emergency Leave") { upd.emergency_leave_count = (upd.emergency_leave_count || 0) + days; }

      const returnDate = new Date(leaveReq.endDate);
      returnDate.setDate(returnDate.getDate() + 1);

      await supabase.from("employees").update({
        annual_leave_balance:  upd.annual_leave_balance,
        annual_leave_used:     upd.annual_leave_used,
        sick_leave_count:      upd.sick_leave_count,
        emergency_leave_count: upd.emergency_leave_count,
        last_leave_end_date:   toSQL(returnDate),
        status: leaveReq.type, is_on_leave: true, contract_assets: newAssets,
      }).eq("id", empData.id);

      await supabase.from("employee_leaves").insert([{
        employee_id: empData.id, leave_type: leaveReq.type,
        start_date: leaveReq.startDate, end_date: leaveReq.endDate,
        days_count: days, status: "approved", document_url: docUrl,
        approved_by: currentUser.full_name, reason: leaveReq.reason || "Admin authorized",
      }]);

      await logActivity("LEAVE_AUTHORIZED", `${leaveReq.type} [${days} days] — ${leaveReq.reason || "N/A"}`);
      setLeaveReq({ startDate: "", endDate: "", type: "Annual Leave", reason: "" });
      setLeaveFile(null);
      alert("✅ Leave authorized successfully");
      fetchData();
    } catch (err) { alert(err.message); }
    finally { setIsLoading(false); }
  };

  // ── ③ Cancel Leave ──
  const handleCancelLeave = async (leave) => {
    const reason = window.prompt(`Enter reason for cancelling ${leave.leave_type} (${leave.days_count} days):`);
    if (!reason) return;
    setIsLoading(true);
    try {
      const { data: freshEmp } = await supabase.from("employees").select("*").eq("id", empData.id).single();

      const upd = {};
      const days = Number(leave.days_count) || 0;

      // رجّع الرصيد حسب نوع الإجازة
      if (leave.leave_type === "Annual Leave") {
        upd.annual_leave_balance = (Number(freshEmp.annual_leave_balance) || 0) + days;
        upd.annual_leave_used = Math.max(0, (Number(freshEmp.annual_leave_used) || 0) - days);
      } else if (leave.leave_type === "Sick Leave") {
        upd.sick_leave_count = Math.max(0, (Number(freshEmp.sick_leave_count) || 0) - days);
      } else if (leave.leave_type === "Emergency Leave") {
        upd.emergency_leave_count = Math.max(0, (Number(freshEmp.emergency_leave_count) || 0) - days);
      }

      // تحقق: هل في إجازة نشطة اليوم غير هذه؟
      const today = new Date().toISOString().split("T")[0];
      const { data: activeLeaves } = await supabase
        .from("employee_leaves")
        .select("id")
        .eq("employee_id", empData.id)
        .eq("status", "approved")
        .lte("start_date", today)
        .gte("end_date", today)
        .neq("id", leave.id);

      // إذا ما في إجازة نشطة أخرى → رجّع Active دائماً
      if (!activeLeaves || activeLeaves.length === 0) {
        upd.status = "Active";
        upd.is_on_leave = false;
        upd.last_leave_end_date = null;
      }

      await supabase.from("employees").update(upd).eq("id", empData.id);
      await supabase.from("employee_leaves").update({ status: "cancelled", rejection_reason: reason }).eq("id", leave.id);
      await logActivity("LEAVE_CANCELLED", `${leave.leave_type} [${days}D] cancelled | Reason: ${reason}`);
      alert("✅ Leave cancelled and balance restored");
      fetchData();
    } catch (err) { alert(err.message); }
    finally { setIsLoading(false); }
  };

  // ── Auto-fix: رجّع Active إذا خلصت الإجازة تلقائياً ──
  const autoFixStatus = async (emp) => {
    if (!emp || emp.status === "Active") return;
    const today = new Date().toISOString().split("T")[0];
    const { data: activeLeaves } = await supabase
      .from("employee_leaves")
      .select("id")
      .eq("employee_id", emp.id)
      .eq("status", "approved")
      .lte("start_date", today)
      .gte("end_date", today);
    if (!activeLeaves || activeLeaves.length === 0) {
      await supabase.from("employees")
        .update({ status: "Active", is_on_leave: false, last_leave_end_date: null })
        .eq("id", emp.id);
    }
  };

  // ── ④ Save edits ──
  const handleSave = async () => {
    // 1. Detect changes
    const changes = [];
    const fieldsToCheck = {
      full_name: "Name",
      job_title: "Job Title",
      department: "Department",
      basic_salary: "Salary",
      phone_number: "Phone",
      email: "Email",
      role: "Role",
      status: "Status",
      housing_allowance: "Housing",
      transportation_allowance: "Transport",
      other_allowance: "Other Allow.",
      employee_number: "Emp. Number",
      joining_date: "Joining Date"
    };

    Object.keys(fieldsToCheck).forEach(key => {
      if (tempData[key] !== empData[key]) {
        changes.push(`${fieldsToCheck[key]}: ${empData[key] || "—"} → ${tempData[key] || "—"}`);
      }
    });

    const details = changes.length > 0 
      ? `Updated: ${changes.join(" | ")}` 
      : "Manual save (No changes detected)";

    // 2. Update DB
    await supabase.from("employees").update(tempData).eq("id", empData.id);
    
    // Clear session cache so permissions update immediately
    try {
      sessionStorage.removeItem("core_admin_user_profile");
      sessionStorage.removeItem("core_staff_access_cache");
      localStorage.removeItem("core_admin_user_profile");
      localStorage.removeItem("core_staff_access_cache");
    } catch {}
    
    setIsEditing(false);
    
    // 3. Log with details
    await logActivity("PROFILE_UPDATED", details);
    
    fetchData();
  };

  // ── ⑤ File vault ──
  const handleUpload = async (file) => {
    if (!file) return;
    setIsLoading(true);
    const path = `${empData.id}/vault_${Date.now()}_${file.name}`;
    await supabase.storage.from("contracts").upload(path, file);
    const { data: { publicUrl } } = supabase.storage.from("contracts").getPublicUrl(path);
    const newAssets = [...(empData.contract_assets || []), { name: file.name, url: publicUrl, path, date: new Date().toISOString() }];
    await supabase.from("employees").update({ contract_assets: newAssets }).eq("id", empData.id);
    await logActivity("FILE_UPLOADED", `Uploaded: ${file.name}`);
    fetchData();
  };

  const handleDeleteFile = async (filePath) => {
    if (!window.confirm("Delete this file from the vault?")) return;
    const filtered = empData.contract_assets.filter(a => a.path !== filePath);
    await supabase.from("employees").update({ contract_assets: filtered }).eq("id", empData.id);
    await supabase.storage.from("contracts").remove([filePath]);
    await logActivity("FILE_DELETED", `Deleted: ${filePath}`);
    fetchData();
  };

  // ── ⑥ Print leave history ──
  const printLeaveHistory = () => {
    const rows = leaveHistory.map(l =>
      `<tr>
        <td style="padding:10px 16px;border-bottom:1px solid #eee;">${l.leave_type}</td>
        <td style="padding:10px 16px;border-bottom:1px solid #eee;">${l.reason || "—"}</td>
        <td style="padding:10px 16px;border-bottom:1px solid #eee;">${fmt(l.start_date)} → ${fmt(l.end_date)}</td>
        <td style="padding:10px 16px;border-bottom:1px solid #eee;text-align:center;">${l.days_count}</td>
        <td style="padding:10px 16px;border-bottom:1px solid #eee;">${l.status}</td>
      </tr>`
    ).join("");

    const win = window.open("", "_blank");
    win.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Leave Report — ${empData.full_name}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Arial, sans-serif; color: #111; background: #fff; padding: 40px; }
          .header { border-bottom: 2px solid #3201e9; padding-bottom: 24px; margin-bottom: 32px; display: flex; justify-content: space-between; align-items: flex-end; }
          .header h1 { font-size: 22px; font-weight: 300; letter-spacing: 2px; text-transform: uppercase; color: #3201e9; }
          .header .meta { font-size: 11px; color: #999; text-align: right; line-height: 1.8; }
          .employee-card { display: flex; gap: 32px; background: #f8f9ff; border: 1px solid #e0e7ff; border-radius: 12px; padding: 24px; margin-bottom: 32px; }
          .employee-card .field { flex: 1; }
          .employee-card .label { font-size: 9px; text-transform: uppercase; letter-spacing: 2px; color: #999; margin-bottom: 4px; }
          .employee-card .value { font-size: 14px; font-weight: 500; color: #111; }
          table { width: 100%; border-collapse: collapse; font-size: 13px; }
          thead tr { background: #3201e9; color: #fff; }
          thead th { padding: 12px 16px; text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: 1px; font-weight: 500; }
          tbody tr:nth-child(even) { background: #f8f9ff; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #eee; display: flex; justify-content: space-between; font-size: 10px; color: #ccc; }
          @media print {
            body { padding: 20px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <h1>CORE Professional</h1>
            <div style="font-size:12px;color:#666;margin-top:6px;letter-spacing:1px;">LEAVE REGISTRY REPORT</div>
          </div>
          <div class="meta">
            Generated: ${new Date().toLocaleDateString("en-GB")}<br>
            Time: ${new Date().toLocaleTimeString("en-GB")}<br>
            Authorized by: ${currentUser?.full_name || "Admin"}
          </div>
        </div>
        <div class="employee-card">
          <div class="field"><div class="label">Full Name</div><div class="value">${empData.full_name}</div></div>
          <div class="field"><div class="label">Employee ID</div><div class="value">#${empData.employee_number || "—"}</div></div>
          <div class="field"><div class="label">Position</div><div class="value">${empData.job_title || "—"}</div></div>
          <div class="field"><div class="label">Department</div><div class="value">${empData.department || "—"}</div></div>
          <div class="field"><div class="label">Annual Balance</div><div class="value">${empData.annual_leave_balance || 0} Days</div></div>
        </div>
        <table>
          <thead>
            <tr>
              <th>Leave Type</th><th>Reason</th><th>Period</th><th style="text-align:center;">Days</th><th>Status</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
        <div class="footer">
          <span>CORE Professional — Workforce Management System</span>
          <span>Total records: ${leaveHistory.length}</span>
        </div>
        <script>window.onload = () => { window.print(); }</` + `>` + `</script>
      </body>
      </html>
    `);
    win.document.close();
  };

  if (!isAuthorized) return (
    <div className="p-20 text-center text-core-charcoal/50 font-light tracking-[10px] animate-pulse bg-gray-50 min-h-screen">
      LOADING...
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6 md:p-10 font-sans text-left" dir="ltr">

      {/* ── HEADER ── */}
      <div className="mb-10 flex flex-col md:flex-row items-center justify-between gap-6 border-b border-gray-200 pb-10">
        <div className="flex items-center gap-8">
          <div className="h-28 w-28 rounded-[35px] border border-core-accent/30 bg-white overflow-hidden shadow-2xl">
            <img
              src={empData?.avatar_url || "/default-avatar.png"}
              onError={e => { e.target.src = "https://ui-avatars.com/api/?name=" + encodeURIComponent(empData?.full_name || "E") + "&background=3201e9&color=fff&size=128"; }}
              className="h-full w-full object-cover" alt="Profile"
            />
          </div>
          <div>
            <h1 className="text-3xl font-light uppercase tracking-wide text-core-charcoal">{empData?.full_name}</h1>
            <p className="mt-1 text-[10px] font-medium uppercase tracking-[0.4em] text-core-accent">
              {empData?.job_title} • #{empData?.employee_number}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className={`rounded-lg px-3 py-1 text-[9px] uppercase font-medium tracking-widest ${statusStyle(empData?.status)}`}>
                {empData?.status}
              </span>
              {empData?.is_on_leave && empData?.last_leave_end_date && (
                <span style={{backgroundColor:"#ea580c",color:"#ffffff",border:"1px solid #c2410c"}} className="rounded-lg px-3 py-1 text-[9px] uppercase font-medium tracking-widest animate-pulse">
                  RETURN: {fmt(empData.last_leave_end_date)}
                </span>
              )}
              {isAdmin && empData?.is_on_leave && (
                <button 
                  onClick={async () => {
                    if (window.confirm("FORCE RESET STATUS: Are you sure? This will manually set the employee status to 'Active' and clear the leave flag. Use this only if the system state is stuck.")) {
                       await supabase.from("employees").update({
                         is_on_leave: false,
                         last_leave_end_date: null,
                         status: "Active"
                       }).eq("id", empData.id);
                       await logActivity("STATUS_FORCE_RESET", "Manually reset status from 'On Leave' to 'Active'");
                       fetchData();
                    }
                  }}
                  className="rounded-lg bg-red-600/20 border border-red-500/50 px-3 py-1 text-[9px] text-red-400 uppercase font-medium tracking-widest hover:bg-red-600 hover:text-white transition-all"
                  title="Force reset status if stuck on leave"
                >
                  Force Reset
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-3 flex-wrap">
          {isAdmin && !isEditing && (
            <>
              <button onClick={() => { setNewPwd(""); setConfirmPwd(""); setPwdErr(""); setShowPwdModal(true); }}
                className="flex items-center gap-2 rounded-2xl bg-blue-50 border border-blue-200 px-6 py-4 text-[10px] uppercase text-blue-600 hover:bg-blue-600 hover:text-white transition-all">
                <MdLockReset size={18} /> Reset Password
              </button>
            </>
          )}
          {isEditing ? (
            <div className="flex gap-2">
              <button onClick={() => { setIsEditing(false); fetchData(); }}
                className="rounded-2xl bg-gray-100 border border-gray-200 px-8 py-4 text-[10px] uppercase text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-all">
                Cancel
              </button>
              <button onClick={handleSave}
                className="bg-emerald-500 rounded-2xl px-10 py-4 text-[10px] font-medium uppercase text-white shadow-xl">
                Save Changes
              </button>
            </div>
          ) : (
            isAdmin && (
              <button onClick={() => setIsEditing(true)}
                className="flex items-center gap-2 rounded-2xl bg-core-accent px-10 py-4 text-[10px] uppercase text-white shadow-xl hover:scale-105 transition-all">
                <MdEdit size={18} /> Edit Profile
              </button>
            )
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        <div className="lg:col-span-3 space-y-8">

          {/* ── IDENTIFICATION ── */}
          <div className="rounded-[40px] border border-gray-200 bg-white p-10 shadow-sm">
            <h3 className="mb-10 flex items-center gap-4 text-[10px] font-medium uppercase tracking-[0.3em] text-gray-400">
              <span className="h-[1px] w-6 bg-core-accent" /> Identification Matrix
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10">
              <MiniInput label="Full Name"      value={tempData?.full_name}       isEditing={isEditing} onChange={v => setTempData({...tempData, full_name: v})}       icon={<MdPerson />} />
              <MiniInput label="Staff ID"       value={tempData?.employee_number} isEditing={isEditing} onChange={v => setTempData({...tempData, employee_number: v})} icon={<MdBadge />} />
              <MiniInput label="Business Email" value={tempData?.email}           isEditing={isEditing} onChange={v => setTempData({...tempData, email: v})}           icon={<MdEmail />} />
              <MiniInput label="National ID"    value={tempData?.national_id}     isEditing={isEditing} onChange={v => setTempData({...tempData, national_id: v})}     icon={<MdFingerprint />} />
              <MiniInput label="Position"       value={tempData?.job_title}       isEditing={isEditing} onChange={v => setTempData({...tempData, job_title: v})}       icon={<MdWork />} />
              <MiniInput label="Basic Salary"   value={tempData?.basic_salary}    isEditing={isEditing} onChange={v => setTempData({...tempData, basic_salary: v})}    icon={<MdAttachMoney />} type="number" />
              <MiniInput label="Hourly Rate"    value={tempData?.hourly_rate}     isEditing={isEditing} onChange={v => setTempData({...tempData, hourly_rate: Number(v) || 0})} icon={<MdTimer />} type="number" step="0.5" />
              <MiniInput label="Phone Number"   value={tempData?.phone_number}    isEditing={isEditing} onChange={v => setTempData({...tempData, phone_number: v})}    icon={<MdPhone />} />
              <MiniInput label="Department"     value={tempData?.department}      isEditing={isEditing} onChange={v => setTempData({...tempData, department: v})}      icon={<MdAccountTree />} />
              <MiniDate  label="Contract Start" value={tempData?.contract_start}  isEditing={isEditing} onChange={v => setTempData({...tempData, contract_start: v})} />
              <MiniDate  label="Contract End"   value={tempData?.contract_end}    isEditing={isEditing} onChange={v => setTempData({...tempData, contract_end: v})} />
            </div>
          </div>

          {/* ── LEAVE HISTORY ── */}
          <div className="rounded-[40px] border border-gray-200 bg-white p-10 shadow-sm">
            <div className="mb-10 flex items-center justify-between">
              <h3 className="flex items-center gap-4 text-[10px] font-medium uppercase tracking-[0.3em] text-gray-400">
                <MdHistory size={20} /> Leave Registry
              </h3>
              <div className="flex gap-2">
                <button onClick={printLeaveHistory}
                  className="flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-[9px] uppercase text-gray-600 hover:text-core-charcoal transition-all">
                  <MdPrint size={16} /> Print Report
                </button>
                {isAdmin && (
                  <button onClick={() => {
                    if (window.confirm("Wipe all leave history?")) {
                      supabase.from("employee_leaves").delete().eq("employee_id", empData.id).then(() => fetchData());
                    }
                  }} className="flex items-center gap-2 rounded-lg bg-red-50 px-4 py-2 text-[9px] uppercase text-red-500 hover:bg-red-500 hover:text-white transition-all">
                    <MdDeleteSweep size={16} /> Wipe All
                  </button>
                )}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[10px] text-gray-500 text-left border-collapse">
                <thead>
                  <tr className="border-b border-gray-200 uppercase tracking-widest opacity-70">
                    <th className="pb-4">Type</th>
                    <th className="pb-4">Reason</th>
                    <th className="pb-4">Period</th>
                    <th className="pb-4 text-center">Days</th>
                    <th className="pb-4">Status</th>
                    {isAdmin && <th className="pb-4 text-right">Action</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {leaveHistory.map(l => (
                    <tr key={l.id} className="group hover:bg-gray-50 transition-colors">
                      <td className="py-5 font-medium text-core-charcoal">{l.leave_type}</td>
                      <td className="py-5 italic text-gray-500">
                        {l.reason || "Personal"}
                        {l.status === 'cancelled' && l.rejection_reason && (
                          <div className="mt-1 text-[9px] text-red-500/80">
                            Cancelled: {l.rejection_reason}
                          </div>
                        )}
                      </td>
                      <td className="py-5 text-gray-600">{fmt(l.start_date)} → {fmt(l.end_date)}</td>
                      <td className="py-5 text-center font-medium text-core-charcoal">{l.days_count}D</td>
                      <td className="py-5 text-emerald-600 uppercase tracking-tighter font-medium">{l.status}</td>
                      {isAdmin && (
                        <td className="py-5 text-right">
                          <button onClick={() => handleCancelLeave(l)}
                            className="text-red-400 hover:text-red-600 transition-all" title="Cancel & Restore">
                            <MdCancel size={18} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                  {leaveHistory.length === 0 && (
                    <tr><td colSpan={6} className="py-10 text-center text-gray-400 uppercase tracking-widest">No leave records</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── AUDIT TRAIL ── */}
          <div className="rounded-[40px] border border-gray-200 bg-white p-10 shadow-sm">
            <div className="mb-8 flex items-center justify-between">
              <h3 className="flex items-center gap-4 text-[10px] font-medium uppercase tracking-[0.3em] text-gray-400">
                <MdTerminal size={20} className="text-emerald-500" /> Audit Trail
              </h3>
              {isAdmin && (
                <button onClick={() => {
                  if (window.confirm("Clear all audit logs?")) {
                    supabase.from("employee_logs").delete().eq("employee_id", empData.id).then(() => fetchData());
                  }
                }} className="text-[9px] text-red-400 uppercase tracking-widest hover:text-red-600 transition-all">
                  Purge Trail
                </button>
              )}
            </div>
            <div className="space-y-3 max-h-80 overflow-y-auto custom-scrollbar pr-4 font-mono">
              {logs.map(log => (
                <div key={log.id} className="p-4 rounded-xl bg-gray-50 border border-gray-100 flex flex-col gap-2 hover:border-core-accent/40 transition-all">
                  <div className="flex justify-between items-center">
                    <span className={`text-[9px] px-2 py-0.5 rounded border uppercase font-medium
                      ${log.action_type.includes("LEAVE") ? "text-blue-600 border-blue-200 bg-blue-50" :
                        log.action_type.includes("RESET") ? "text-orange-600 border-orange-200 bg-orange-50" :
                        "text-emerald-600 border-emerald-200 bg-emerald-50"}`}>
                      {log.action_type}
                    </span>
                    <span className="text-[8px] text-gray-400 uppercase tracking-widest">
                      {new Date(log.created_at).toLocaleString("en-GB")}
                    </span>
                  </div>
                  <pre className="text-[10px] text-gray-500 whitespace-pre-wrap leading-relaxed italic">{log.details}</pre>
                </div>
              ))}
              {logs.length === 0 && (
                <p className="text-center text-gray-400 text-xs uppercase tracking-widest py-8">No audit records</p>
              )}
            </div>
          </div>

          {/* ── DIGITAL VAULT ── */}
          <div className="rounded-[40px] border border-gray-200 bg-white p-10 shadow-sm">
            <div className="mb-10 flex items-center justify-between">
              <h3 className="flex items-center gap-4 text-[10px] font-medium uppercase tracking-[0.3em] text-gray-400">
                <MdInsertDriveFile size={20} /> Digital Vault
              </h3>
              {isAdmin && (
                <label className="cursor-pointer rounded-lg bg-core-accent/10 border border-core-accent/20 px-4 py-2 text-[9px] uppercase text-core-accent hover:bg-core-accent hover:text-white transition-all">
                  <MdCloudUpload size={16} className="inline mr-2" /> Upload File
                  <input type="file" className="hidden" onChange={e => handleUpload(e.target.files[0])} />
                </label>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(empData?.contract_assets || []).map((file, idx) => (
                <div key={idx} className="group relative rounded-3xl border border-gray-100 bg-gray-50 p-6 hover:border-core-accent/30 transition-all">
                  <span className="text-[9px] font-medium uppercase text-core-charcoal truncate block mb-1">{file.name}</span>
                  <span className="text-[8px] text-gray-400 uppercase tracking-tighter">{fmt(file.date)}</span>
                  <div className="mt-4 flex gap-2">
                    <a href={file.url} target="_blank" rel="noreferrer"
                      className="flex-1 rounded-lg bg-core-accent/10 py-2 text-center text-[9px] uppercase text-core-accent hover:bg-core-accent hover:text-white transition-all">
                      Open
                    </a>
                    {isAdmin && (
                      <button onClick={() => handleDeleteFile(file.path)}
                        className="rounded-lg bg-red-50 px-3 py-2 text-red-500 opacity-0 group-hover:opacity-100 transition-all hover:bg-red-100">
                        <MdDelete size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {!(empData?.contract_assets?.length) && (
                <p className="col-span-3 text-center text-gray-400 text-xs uppercase tracking-widest py-8">No files uploaded</p>
              )}
            </div>
          </div>
        </div>

        {/* ── SIDEBAR ── */}
        <div className="space-y-8">

          {/* Control Center — permissions */}
          {isAdmin && (
            <div className="rounded-[40px] border border-gray-200 bg-white p-8 shadow-xl">
              <h3 className="mb-2 text-[11px] font-medium uppercase tracking-widest text-core-accent">Control Center</h3>
              <p className="mb-6 text-[9px] text-gray-400 uppercase tracking-wider">
                Save changes for permissions to take effect on next login
              </p>
              <div className="space-y-1">
                <Toggle label="Promote Admin"     active={tempData?.role === "admin"}                          isEditing={isEditing} onChange={v => setTempData({...tempData, role: v ? "admin" : "employee"})}                                                          icon={<MdSettingsSuggest />} />
                <Toggle label="Staff Management"  active={tempData?.permissions?.can_access_staff}             isEditing={isEditing} onChange={v => setTempData({...tempData, permissions: {...tempData.permissions, can_access_staff: v}})}             icon={<MdPeople />} />
                <Toggle label="Requests"          active={tempData?.permissions?.can_access_leave_requests}    isEditing={isEditing} onChange={v => setTempData({...tempData, permissions: {...tempData.permissions, can_access_leave_requests: v}})}    icon={<MdDateRange />} />
                <Toggle label="CRM - Leads"       active={tempData?.permissions?.can_access_crm}               isEditing={isEditing} onChange={v => setTempData({...tempData, permissions: {...tempData.permissions, can_access_crm: v}})}               icon={<MdAssignmentInd />} />
                <Toggle label="Articles CMS"      active={tempData?.permissions?.can_access_cms}               isEditing={isEditing} onChange={v => setTempData({...tempData, permissions: {...tempData.permissions, can_access_cms: v}})}               icon={<MdDescription />} />
              </div>
            </div>
          )}

          {/* Vacation Hub */}
          <div className="rounded-[40px] border border-gray-200 bg-white p-8 shadow-xl">
            <h3 className="mb-8 flex items-center gap-3 text-[11px] font-medium uppercase tracking-widest text-emerald-600">
              <MdFlightTakeoff size={24} /> Vacation Hub
            </h3>
            <div className="space-y-4">
              <MiniDate label="Start Date" value={leaveReq.startDate} isEditing={true} onChange={v => setLeaveReq({...leaveReq, startDate: v})} />
              <MiniDate label="End Date"   value={leaveReq.endDate}   isEditing={true} onChange={v => setLeaveReq({...leaveReq, endDate: v})} />
              <div className="flex flex-col gap-2">
                <label className="text-[9px] font-medium uppercase text-gray-400 ml-1">Reason</label>
                <div className="relative flex items-center rounded-2xl border border-gray-200 bg-gray-50 focus-within:border-core-accent/40 transition-all">
                  <div className="absolute left-4 text-emerald-500/40"><MdComment size={16} /></div>
                  <input value={leaveReq.reason} onChange={e => setLeaveReq({...leaveReq, reason: e.target.value})}
                    placeholder="Enter reason..." className="w-full bg-transparent p-4 pl-12 text-[11px] font-light text-core-charcoal outline-none placeholder:text-gray-400" />
                </div>
              </div>
              <select value={leaveReq.type} onChange={e => setLeaveReq({...leaveReq, type: e.target.value})}
                className="w-full rounded-2xl border border-gray-200 bg-gray-50 p-4 text-[10px] text-core-charcoal outline-none focus:border-core-accent/40 transition-all">
                <option value="Annual Leave">Annual Leave</option>
                <option value="Sick Leave">Sick Leave</option>
                <option value="Emergency Leave">Emergency Leave</option>
              </select>
              <label className="flex w-full cursor-pointer items-center justify-center gap-3 rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-4 text-gray-400 hover:bg-gray-100 transition-all">
                <MdCloudUpload size={20} />
                <span className="text-[10px] font-medium uppercase truncate">{leaveFile ? leaveFile.name : "Upload Document"}</span>
                <input type="file" className="hidden" onChange={e => setLeaveFile(e.target.files[0])} />
              </label>
              {isAdmin && (
                <button onClick={handleLeaveRequest} disabled={isLoading}
                  className="w-full rounded-2xl bg-core-accent py-5 text-[10px] font-medium uppercase text-white shadow-xl hover:scale-105 transition-all disabled:opacity-50">
                  {isLoading ? "Processing..." : "Authorize Leave"}
                </button>
              )}
            </div>

            {/* Leave balances */}
            <div className="mt-8 pt-8 border-t border-gray-100 space-y-4">
              <BalRow label="Annual Balance" value={tempData?.annual_leave_balance} color="#10b981" isEditing={isEditing} onChange={v => setTempData({...tempData, annual_leave_balance: parseInt(v)})} />
              <BalRow label="Days Used"      value={tempData?.annual_leave_used}    color="#3b82f6" isEditing={isEditing} onChange={v => setTempData({...tempData, annual_leave_used: parseInt(v)})} />
              <BalRow label="Sick Days"      value={tempData?.sick_leave_count}     color="#8b5cf6" isEditing={isEditing} onChange={v => setTempData({...tempData, sick_leave_count: parseInt(v)})} />
              <BalRow label="Emergency Days" value={tempData?.emergency_leave_count}color="#f97316" isEditing={isEditing} onChange={v => setTempData({...tempData, emergency_leave_count: parseInt(v)})} />
            </div>
          </div>
        </div>
      </div>

      {/* ══════════ MODAL: RESET PASSWORD ══════════ */}
      {showPwdModal && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-[32px] border border-gray-200 bg-white p-10 shadow-2xl">
            <div className="text-center mb-8">
              <div className="text-4xl mb-4">🔑</div>
              <h3 className="text-xl font-light uppercase italic text-core-charcoal">Reset Password</h3>
              <p className="mt-1 text-sm text-gray-400">{empData?.full_name}</p>
            </div>
            {pwdErr && (
              <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">❌ {pwdErr}</div>
            )}
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <label className="text-[10px] uppercase tracking-widest text-gray-400">New Password</label>
                <input type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)}
                  placeholder="Min 6 characters"
                  className="w-full rounded-2xl border border-gray-200 bg-gray-50 p-5 text-sm text-core-charcoal outline-none focus:border-core-accent/50 placeholder:text-gray-400" />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-[10px] uppercase tracking-widest text-gray-400">Confirm Password</label>
                <input type="password" value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)}
                  placeholder="Repeat password"
                  className="w-full rounded-2xl border border-gray-200 bg-gray-50 p-5 text-sm text-core-charcoal outline-none focus:border-core-accent/50 placeholder:text-gray-400" />
              </div>
            </div>
            <div className="mt-8 flex gap-4">
              <button onClick={() => { setShowPwdModal(false); setPwdErr(""); }}
                className="flex-1 rounded-2xl bg-gray-100 py-4 text-xs uppercase text-gray-500 hover:bg-gray-200 transition-all">
                Cancel
              </button>
              <button onClick={handleResetPassword} disabled={isLoading}
                className="flex-1 rounded-2xl bg-core-accent py-4 text-xs uppercase text-white hover:brightness-110 active:scale-95 transition-all disabled:opacity-50">
                {isLoading ? "Updating..." : "Update Password"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────
const MiniInput = ({ label, value, isEditing, onChange, icon, type = "text", step }) => (
  <div className="flex flex-col gap-3">
    <label className="text-[9px] font-medium uppercase tracking-widest text-gray-400">{label}</label>
    <div className={`relative flex items-center rounded-2xl border transition-all ${isEditing ? "border-core-accent/30 bg-core-accent/5" : "border-gray-200 bg-gray-50"}`}>
      <div className="absolute left-4 text-core-accent/40">{icon}</div>
      <input disabled={!isEditing} type={type} step={step} value={value || ""} onChange={e => onChange(e.target.value)}
        className="w-full bg-transparent p-4 pl-12 text-[12px] font-light text-core-charcoal outline-none disabled:text-gray-500" />
    </div>
  </div>
);

const MiniDate = ({ label, value, isEditing, onChange }) => (
  <div className="flex flex-col gap-3 text-left">
    <label className="text-[9px] font-medium uppercase tracking-widest text-gray-400">{label}</label>
    <div className={`rounded-2xl border transition-all ${isEditing ? "border-core-accent/30 bg-core-accent/5" : "border-gray-200 bg-gray-50"}`}>
      <Suspense fallback={<div className="p-4 text-[10px] text-gray-400">Loading...</div>}>
        <FlatpickrLazy value={value} disabled={!isEditing}
          options={{ dateFormat: "Y-m-d" }}
          className="w-full bg-transparent p-4 text-[12px] font-light text-core-charcoal outline-none disabled:text-gray-500"
          onChange={([d]) => { if (d) { const y = d.getFullYear(); const m = String(d.getMonth()+1).padStart(2,"0"); const day = String(d.getDate()).padStart(2,"0"); onChange(`${y}-${m}-${day}`); }}} />
      </Suspense>
    </div>
  </div>
);

const BalRow = ({ label, value, color, isEditing, onChange }) => (
  <div className="flex items-center justify-between py-2 border-b border-gray-100">
    <span className="text-[10px] font-medium uppercase text-gray-400 tracking-wider">{label}</span>
    {isEditing ? (
      <input type="number" value={value || 0} onChange={e => onChange(e.target.value)}
        className="w-16 bg-gray-50 border border-gray-200 rounded text-center text-core-charcoal text-xs p-1 outline-none focus:border-core-accent" />
    ) : (
      <span className="text-xl font-light italic" style={{ color }}>{value || 0}D</span>
    )}
  </div>
);

const Toggle = ({ label, active, isEditing, onChange, icon }) => (
  <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
    <div className="flex items-center gap-3">
      <span className={active ? "text-core-accent" : "text-gray-300"}>{icon}</span>
      <span className={`text-[10px] uppercase font-medium ${active ? "text-core-charcoal" : "text-gray-400"}`}>{label}</span>
    </div>
    <button disabled={!isEditing} onClick={() => onChange(!active)}
      className={`relative h-6 w-11 rounded-full transition-all ${active ? "bg-core-accent" : "bg-gray-200"} ${!isEditing && "opacity-50 cursor-not-allowed"}`}>
      <div className={`absolute top-1 h-4 w-4 rounded-full bg-white transition-all shadow-sm ${active ? "left-6" : "left-1"}`} />
    </button>
  </div>
);